import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SectionCard } from "../../../../components/common/section-card";
import { EmptyState } from "../../../../components/common/empty-state";
import { IconStar } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { AdminLoyaltyTransactionDto } from "../../../../api";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodyXs" tone="muted">{label}</Text>
      <Text variant="body" tone="dark">{value}</Text>
    </div>
  );
}

export function AdminLoyaltyTransactionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const tx = location.state?.transaction as AdminLoyaltyTransactionDto | undefined;

  if (!tx) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <EmptyState
          icon={IconStar}
          title="Transaction not found"
          description="Navigate here from the Loyalty Points page."
          primaryAction={{
            label: "Back to Loyalty Points",
            onClick: () => navigate(AppRoutes.ADMIN_LOYALTY_CONFIG),
          }}
        />
      </div>
    );
  }

  const isExpiringSoon =
    !tx.isExpired && (new Date(tx.expiresAt).getTime() - Date.now()) < 30 * 24 * 60 * 60 * 1000;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(AppRoutes.ADMIN_LOYALTY_CONFIG)}
          className="hover:underline"
        >
          <Text variant="bodyBold" tone="accent">← Loyalty Points</Text>
        </button>
        <Text variant="body" tone="muted">/</Text>
        <Text variant="body" tone="muted">Transaction {id}</Text>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Heading variant="h2" tone="dark">Transaction {id}</Heading>
          {tx.isExpired ? (
            <Chip variant="rejected">Expired</Chip>
          ) : tx.isRedeemed ? (
            <Chip variant="secondary">Redeemed</Chip>
          ) : isExpiringSoon ? (
            <Chip variant="underReview">Expiring soon</Chip>
          ) : (
            <Chip variant="approved">Active</Chip>
          )}
        </div>
        <Text variant="bodySm" tone="muted">{formatDate(tx.createdAt)}</Text>
      </div>

      <SectionCard title="Transaction details">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Field label="Transaction ID" value={tx.id} />
          <Field label="Points" value={tx.points.toLocaleString()} />
          <Field label="Amount" value={`${tx.currency} ${tx.amount.toLocaleString()}`} />
          <Field label="Currency" value={tx.currency} />
          <Field label="Expires at" value={formatDate(tx.expiresAt)} />
          <Field label="Created at" value={formatDate(tx.createdAt)} />
          <Field label="Is expired" value={tx.isExpired ? "Yes" : "No"} />
          <Field label="Is redeemed" value={tx.isRedeemed ? "Yes" : "No"} />
        </div>
      </SectionCard>

      <SectionCard title="User details">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Field label="User ID" value={tx.userId} />
          <Field label="Partner name" value={tx.partnerName ?? "—"} />
          <Field label="Full name" value={tx.userName ?? "—"} />
          <Field label="Email" value={tx.email ?? "—"} />
          <Field label="Phone" value={tx.phoneNumber ?? "—"} />
        </div>
      </SectionCard>
    </div>
  );
}
